/**
 * 服务操作业务逻辑 Hook
 * 包含带业务逻辑的服务操作方法
 */

import { useCallback } from "react";
import { message, Modal } from "antd";
import { ServiceRecord } from "../service-apis/serviceTypes";
import { ServiceApiService } from "../service-apis/serviceManagementService";
import { validateServiceRecord, validateHealthCheckUrl } from "../utils/serviceManagementUtils";
import { parentComm } from "@/utils/parentCommunication";

export const useServiceOperations = () => {
  /**
   * 处理服务创建（带业务逻辑）
   */
  const handleServiceCreate = useCallback(
    async (values: any, onSuccess: () => void): Promise<boolean> => {
      try {
        const response = await ServiceApiService.createService(values);
        if (response.success) {
          onSuccess();
          message.success("服务创建成功");
          // 通知父窗口（msLogin）刷新服务列表
          if (parentComm.isInIframe()) {
            window.parent.postMessage(
              {
                type: "SERVICES_UPDATED",
                action: "create",
                serviceName: values.name || values.service_name,
                timestamp: Date.now(),
              },
              "*"
            );
            console.log("[useServiceOperations] 已发送SERVICES_UPDATED消息（创建）");
          }
          return true;
        } else {
          message.error(response.message || "创建服务失败");
          return false;
        }
      } catch (error) {
        console.error("创建服务失败:", error);
        message.error("创建服务失败，请检查网络连接");
        return false;
      }
    },
    []
  );

  /**
   * 处理服务编辑（带业务逻辑）
   */
  const handleServiceEdit = useCallback(
    async (
      record: ServiceRecord,
      values: any,
      onSuccess: () => void
    ): Promise<boolean> => {
      if (!validateServiceRecord(record)) {
        return false;
      }

      try {
        const response = await ServiceApiService.updateService(record.id, values);
        if (response.success) {
          onSuccess();
          message.success("服务更新成功");
          // 通知父窗口（msLogin）刷新服务列表
          if (parentComm.isInIframe()) {
            window.parent.postMessage(
              {
                type: "SERVICES_UPDATED",
                action: "update",
                serviceName: values.name || values.service_name || record.name,
                timestamp: Date.now(),
              },
              "*"
            );
            console.log("[useServiceOperations] 已发送SERVICES_UPDATED消息（更新）");
          }
          return true;
        } else {
          message.error(response.message || "更新服务失败");
          return false;
        }
      } catch (error) {
        console.error("更新服务失败:", error);
        message.error("更新服务失败，请检查网络连接");
        return false;
      }
    },
    []
  );

  /**
   * 处理服务状态切换（带业务逻辑）
   */
  const handleServiceStatusToggle = useCallback(
    async (record: ServiceRecord, onSuccess: () => void): Promise<boolean> => {
      if (!validateServiceRecord(record)) {
        return false;
      }

      const newStatus = record.status === "online" ? "offline" : "online";
      const action = newStatus === "online" ? "启动" : "停止";

      try {
        const response = await ServiceApiService.updateService(record.id, {
          name: record.name,
          backendUrl: record.backendUrl,
          url: record.url,
          description: record.description,
          healthCheckUrl: record.healthCheckUrl,
          status: newStatus,
        });

        if (response.success) {
          onSuccess();
          message.success(`服务${action}成功`);
          return true;
        } else {
          message.error(response.message || `服务${action}失败`);
          return false;
        }
      } catch (error) {
        console.error(`切换服务状态失败:`, error);
        message.error(`服务${action}失败，请检查网络连接`);
        return false;
      }
    },
    []
  );

  /**
   * 处理服务删除（带业务逻辑）
   */
  const handleServiceDelete = useCallback(
    async (record: ServiceRecord, onSuccess: () => void): Promise<boolean> => {
      if (!validateServiceRecord(record)) {
        return false;
      }

      // 显示确认对话框
      return new Promise((resolve) => {
        Modal.confirm({
          title: "确认删除服务",
          content: `确定要删除服务 "${record.displayName || record.name || record.service_name}" 吗？此操作不可恢复。`,
          okText: "确认删除",
          okType: "danger",
          cancelText: "取消",
          onOk: async () => {
            try {
              console.log("[useServiceOperations.handleServiceDelete] 开始删除服务:", {
                recordId: record.id,
                recordName: record.name || record.service_name,
                record: record
              });

              if (!record.id) {
                console.error("[useServiceOperations.handleServiceDelete] 服务ID为空:", record);
                message.error("服务ID不存在，无法删除");
                resolve(false);
                return;
              }

              const response = await ServiceApiService.deleteService(record.id);
              console.log("[useServiceOperations.handleServiceDelete] 删除响应:", response);
              
              if (response.success) {
                onSuccess();
                message.success("服务删除成功");
                // 通知父窗口（msLogin）刷新服务列表
                if (parentComm.isInIframe()) {
                  window.parent.postMessage(
                    {
                      type: "SERVICES_UPDATED",
                      action: "delete",
                      serviceName: record.name || record.service_name,
                      timestamp: Date.now(),
                    },
                    "*"
                  );
                  console.log("[useServiceOperations] 已发送SERVICES_UPDATED消息（删除）");
                }
                resolve(true);
              } else {
                const errorMsg = response.message || "删除服务失败";
                console.error("[useServiceOperations.handleServiceDelete] 删除失败:", errorMsg);
                message.error(errorMsg);
                resolve(false);
              }
            } catch (error: any) {
              console.error("[useServiceOperations.handleServiceDelete] 删除异常:", {
                error,
                recordId: record.id,
                response: error.response?.data,
                status: error.response?.status
              });
              
              const errorMsg = error.response?.data?.message || error.message || "删除服务失败，请检查网络连接";
              message.error(errorMsg);
              resolve(false);
            }
          },
          onCancel: () => {
            resolve(false);
          },
        });
      });
    },
    []
  );

  /**
   * 处理健康检查（带业务逻辑）
   */
  const handleHealthCheck = useCallback(
    async (
      record: ServiceRecord,
      onSuccess: () => void,
      onError: (error: any) => void
    ): Promise<boolean> => {
      console.log('[健康检查] 开始健康检查，服务:', record.name, {
        id: record.id,
        healthCheckUrl: record.healthCheckUrl,
        backendUrl: record.backendUrl
      });

      if (!validateHealthCheckUrl(record)) {
        console.warn('[健康检查] 验证失败，服务:', record.name, {
          healthCheckUrl: record.healthCheckUrl,
          backendUrl: record.backendUrl
        });
        message.warning("该服务未配置健康检查地址和后端地址，无法进行健康检查");
        return false;
      }

      console.log('[健康检查] 验证通过，开始执行健康检查');

      try {
        const loadingKey = "health-check-loading";
        message.loading({
          content: "正在检测服务健康状态...",
          key: loadingKey,
          duration: 0,
        });

        console.log('[健康检查] 调用API，服务ID:', record.id);
        const response = await ServiceApiService.healthCheck(record.id);
        console.log('[健康检查] API响应:', response);
        message.destroy(loadingKey);

        if (response.success) {
          const healthStatus = response.data?.healthStatus;
          const statusCode = response.data?.statusCode;
          const responseTime = response.data?.responseTime;
          const errorMessage = response.data?.errorMessage || response.data?.message;

          if (healthStatus) {
            const timeText = responseTime !== undefined ? ` (响应时间: ${responseTime}ms)` : statusCode ? ` (状态码: ${statusCode})` : '';
            message.success(`健康检查完成：已通过${timeText}`);
          } else {
            message.error(`健康检查完成：未通过${errorMessage ? ` - ${errorMessage}` : ""}`);
          }

          onSuccess();
          return healthStatus;
        } else {
          message.error(response.message || "健康检查失败");
          onError(new Error(response.message || "健康检查失败"));
          return false;
        }
      } catch (error) {
        message.destroy("health-check-loading");
        console.error("健康检测异常:", error);
        message.error("健康检查失败，请稍后重试");
        onError(error);
        return false;
      }
    },
    []
  );

  return {
    handleServiceCreate,
    handleServiceEdit,
    handleServiceStatusToggle,
    handleServiceDelete,
    handleHealthCheck,
  };
};
